import React, { useState, useMemo } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiCheck } from 'react-icons/fi';
import Swal from 'sweetalert2';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { useWhatWeOffer, useCreateWhatWeOffer, useUpdateWhatWeOffer, useDeleteWhatWeOffer } from '../hooks/useWhatWeOffer';

const WhatWeOffer = ({ user, onLogout }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState([]);

  // Fetch data using React Query
  const {
    data: whatWeOfferResponse,
    isLoading,
    error,
    isFetching,
  } = useWhatWeOffer();

  console.log("dsfsd",whatWeOfferResponse);

  // Mutations
  const createMutation = useCreateWhatWeOffer();
  const updateMutation = useUpdateWhatWeOffer();
  const deleteMutation = useDeleteWhatWeOffer();

  // Initialize edited data when data is loaded
  React.useEffect(() => {
    if (whatWeOfferResponse && !isEditing) {
      const data = whatWeOfferResponse.data || [];
      setEditedData([...data]);
    }
  }, [whatWeOfferResponse, isEditing]);

  // Check if mutation is loading
  const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // Get error message from mutations or query
  const errorMessage = error?.message || 
                      createMutation.error?.message || 
                      updateMutation.error?.message || 
                      deleteMutation.error?.message;

  const handleEdit = () => {
    setIsEditing(true);
    const data = whatWeOfferResponse?.data || [];
    setEditedData([...data]);
  };

  const handleCancel = () => {
    setIsEditing(false);
    const data = whatWeOfferResponse?.data || [];
    setEditedData([...data]);
  };

  const handleSave = async () => {
    try {
      // Filter out empty items
      const validItems = editedData.filter(item => 
        item.en?.trim() || item.ar?.trim()
      );

      if (validItems.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Warning',
          text: 'Please add at least one item with content.',
        });
        return;
      }

      // Send the entire points array
      await createMutation.mutateAsync({ points: validItems });
      setIsEditing(false);
      
      Swal.fire({
        title: 'Success!',
        text: 'What we offer points saved successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire('Error!', error.message, 'error');
    }
  };

  const handleTextChange = (index, value) => {
    setEditedData(prev => {
      const newData = [...prev];
      if (!newData[index]) {
        newData[index] = { ar: '', en: '' };
      }
      newData[index] = {
        ...newData[index],
        en: value,
        ar: value // Auto-set Arabic to same as English
      };
      return newData;
    });
  };

  const addNewItem = () => {
    setEditedData(prev => [
      ...prev,
      { ar: '', en: '' }
    ]);
  };

  const removeItem = (index) => {
    setEditedData(prev => prev.filter((_, i) => i !== index));
  };

  // Handle loading state
  if (isLoading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      </Layout>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="space-y-6">
          <PageHeader
            title="What We Offer"
            description="Manage what we offer student page data"
          />
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-red-800">{error.message}</div>
          </div>
        </div>
      </Layout>
    );
  }

  const displayData = whatWeOfferResponse.data || [];
  console.log(displayData);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-0">
        <PageHeader
          title="What We Offer"
          description="Manage what we offer student page data"
          action={{
            label: 'Edit Points',
            onClick: handleEdit,
            icon: <FiEdit className="h-4 w-4" />,
          }}
        />

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-700">{errorMessage}</p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">What We Offer Points</h2>
              <div className="flex space-x-3">
                {!isEditing ? (
                  <button
                    onClick={handleEdit}
                    className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-md transition duration-200 flex items-center gap-2"
                  >
                    <FiEdit className="h-4 w-4" />
                    Edit Points
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleCancel}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition duration-200"
                      disabled={isMutating}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={isMutating}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md transition duration-200 disabled:opacity-50 flex items-center gap-2"
                    >
                      <FiCheck className="h-4 w-4" />
                      {isMutating ? 'Saving...' : 'Save Changes'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="p-6">
            {!isEditing ? (
              // Display Mode - List View
              <div className="space-y-4">
                {displayData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No points found. Click Edit Points to add some.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {displayData.map((item, index) => (
                      <div key={index} className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="text-gray-900 font-medium">
                            {item?.en || 'No content set'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // Edit Mode - List View
              <div className="space-y-4">
                {editedData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No points found. Add your first point below.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {editedData.map((item, index) => (
                      <div key={index} className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <textarea
                            value={item?.en || ''}
                            onChange={(e) => handleTextChange(index, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            rows="2"
                            placeholder="Enter point text"
                          />
                        </div>
                        <button
                          onClick={() => removeItem(index)}
                          className="flex-shrink-0 text-red-600 hover:text-red-800 p-2"
                          title="Remove point"
                        >
                          <FiTrash2 className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Point Button */}
                <div className="flex justify-center pt-4">
                  <button
                    onClick={addNewItem}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-md transition duration-200 flex items-center gap-2"
                  >
                    <FiPlus className="h-5 w-5" />
                    Add New Point
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WhatWeOffer; 